<script setup lang="ts">
import { referenceProjectIDs } from '#design-tokenomics/utils/config'
import { formatValue } from '@forgd/formatter'
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'
import { Button } from '@forgd/ui-kit/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@forgd/ui-kit/combobox'
import { InboxIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

const emit = defineEmits<{
  trash: [void]
}>()

type ReferenceProject = SuccessResponseBody<'/coins-with-market-data'>[number]

const { data: projects } = useQuery({
  ...buildApiQuery('/coins-with-market-data', {
    params: {
      query: {
        coingeckoIds: referenceProjectIDs.join(','),
      },
    },
  }),
})

const selectedProject = defineModel<ReferenceProject>()

const { open, query, filtered, reset } = useComboboxFilter(
  projects,
  project => project.name,
)

function selectProject(project: ReferenceProject) {
  selectedProject.value = project
  reset()
}

function trash() {
  emit('trash')
}
</script>

<template>
  <div
    class="rounded-lg border border-default bg-neutral-400 p-5"
  >
    <div v-if="projects" class="flex items-center gap-5">
      <Combobox v-model:open="open">
        <ComboboxTrigger size="sm">
          <span v-if="selectedProject" class="flex flex-1 items-center gap-2 truncate">
            <ProjectAvatar v-if="selectedProject.image" :src="selectedProject.image" size="xs" bordered />
            <span class="truncate">{{ selectedProject.name }}</span>
          </span>
          <span v-else class="flex-1 text-muted-fg">Select an option</span>
        </ComboboxTrigger>
        <ComboboxContent>
          <ComboboxInput v-model="query" placeholder="Search..." />
          <ComboboxList>
            <ComboboxEmpty v-if="filtered.length === 0">
              No results
            </ComboboxEmpty>
            <ComboboxItem
              v-for="project in filtered"
              :key="project.id"
              :value="project.id"
              :selected="selectedProject?.id === project.id"
              @select="selectProject(project)"
            >
              <ProjectAvatar v-if="project.image" :src="project.image" size="sm" bordered />
              <span class="flex-1 truncate">{{ project.name }}</span>
            </ComboboxItem>
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      <Button
        variant="ghost"
        size="sm"
        icon-only
        aria-label="Remove"
        :disabled="!selectedProject"
        @click="trash"
      >
        <TrashIcon aria-hidden="true" />
      </Button>
    </div>

    <div v-if="selectedProject" class="mt-5 space-y-2 pt-px text-sm text-gray-600">
      <div class="flex justify-between">
        <div>Fully Diluted Valuation (FDV)</div>
        <div>{{ formatValue(selectedProject.FDV, 'currency') }}</div>
      </div>
      <div class="flex justify-between">
        <div>Market Capitalization</div>
        <div>{{ formatValue(selectedProject.MC, 'currency') }}</div>
      </div>
    </div>
    <div
      v-else class="mt-2.5 flex flex-col items-center justify-center text-muted [&_svg]:size-10"
    >
      <InboxIcon />
      <span>
        No data available
      </span>
    </div>
  </div>
</template>
